import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { Card, CardBody } from "reactstrap";

import { H3 } from "../../AbstractElements";
import { ALL_USERS_STATUS } from "../../Api/constants";
import FilterSliderModal from "../../CommonElements/FilterSliderModal";
import SearchBar from "../../CommonElements/SearchBar";
import TotalCountCard from "../../container/TotalCountCard";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import { getUsersList } from "../../ReduxToolkit/Reducers/UserSlice";
import {
  OngoingClass,
  Upcoming,
  Completed,
  Select,
  CreatedBy,
  CreatedOn,
  NoOfQuestions,
  Status,
  StartDate,
  EndDate,
  Ongoing,
} from "../../utils/Constant";
import { customTableStyles, formatCustomDate } from "../../utils/helper/helper";
import "./style.scss";
import useIsMobile from "../../utils/helper/responsive";
import useIsTablet from "../../utils/helper/tabView";

const ScheduledSurveys = () => {
  const isMobile = useIsMobile();
  const isTab = useIsTablet();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [activeStatus, setActiveStatus] = useState("");
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const [clearData, setClearData] = useState(false);
  const [totalCountData, setTotalCountData] = useState([
    {
      count: 0,
      title: Ongoing,
      icon: "total-request",
      id: "ongoing",
    },
    {
      count: 0,
      title: Upcoming,
      icon: "approved",
      id: "upcoming",
    },
    {
      count: 0,
      title: Completed,
      icon: "declined",
      id: "completed",
    },
  ]);
  const [userMap, setUserMap] = useState<{ [key: number]: any }>({});
  const { usersList } = useAppSelector(state => state.user);

  // Dummy data for demonstration - replace with actual API data
  const surveyData = [
    {
      id: 1,
      name: "Employee Engagement Survey",
      title: "Annual Employee Engagement",
      description: "Annual engagement survey for all employees",
      created_by: 71778,
      created_on: "2024-06-01T10:00:00Z",
      questions: 12,
      status: "ongoing",
      start_date: "2024-06-01T00:00:00Z",
      end_date: "2024-06-30T23:59:59Z",
    },
    {
      id: 2,
      name: "Training Feedback Survey",
      title: "Training Program Feedback",
      description: "Feedback on recent training programs",
      created_by: 71779,
      created_on: "2024-05-15T14:30:00Z",
      questions: 8,
      status: "upcoming",
      start_date: "2024-07-01T00:00:00Z",
      end_date: "2024-07-15T23:59:59Z",
    },
    {
      id: 3,
      name: "Exit Interview Survey",
      title: "Exit Process Feedback",
      description: "Exit process feedback for departing employees",
      created_by: 71780,
      created_on: "2024-04-20T09:15:00Z",
      questions: 5,
      status: "completed",
      start_date: "2024-05-01T00:00:00Z",
      end_date: "2024-05-31T23:59:59Z",
    },
  ];

  useEffect(() => {
    setActiveStatus("");
    dispatch(getUsersList({ role: ALL_USERS_STATUS }));
  }, [dispatch]);

  useEffect(() => {
    const userMapObj = {};
    usersList?.users?.forEach(user => {
      userMapObj[user.id] = user;
    });
    setUserMap(userMapObj);
  }, [usersList]);

  useEffect(() => {
    setActiveStatus("ongoing");
    // TODO: Replace with actual API call
    // dispatch(getScheduledSurveysList({ status: "ongoing" }));
  }, [dispatch]);

  useEffect(() => {
    // Calculate counts based on survey data
    const ongoingCount = surveyData.filter(
      survey => survey.status === "ongoing"
    ).length;
    const upcomingCount = surveyData.filter(
      survey => survey.status === "upcoming"
    ).length;
    const completedCount = surveyData.filter(
      survey => survey.status === "completed"
    ).length;

    setTotalCountData([
      {
        count: ongoingCount,
        title: OngoingClass,
        icon: "total-request",
        id: "ongoing",
      },
      {
        count: upcomingCount,
        title: Upcoming,
        icon: "approved",
        id: "upcoming",
      },
      {
        count: completedCount,
        title: Completed,
        icon: "declined",
        id: "completed",
      },
    ]);
  }, [surveyData]);

  const handleDone = async (values: { [key: string]: string[] }) => {
    setClearData(true);
    return values;
    // const fromDate = values["start_date"] ? values["start_date"][0] : "";
    // const toDate = values["end_date"] ? values["end_date"][0] : "";
    // const createdByIds = values["created_by"] ? values["created_by"].join(",") : "";

    // const formattedFromDate = fromDate
    //   ? new Date(fromDate).toLocaleDateString("en-CA").split("T")[0]
    //   : "";
    // const formattedToDate = toDate
    //   ? new Date(toDate).toLocaleDateString("en-CA").split("T")[0]
    //   : "";

    // TODO: Replace with actual API call
    // await dispatch(
    //   getScheduledSurveysList({
    //     from_date: formattedFromDate,
    //     to_date: formattedToDate,
    //     created_by: createdByIds,
    //     status: activeStatus,
    //   })
    // );
  };

  const handleClear = async () => {
    setSelectedFilterValues({});
    setDefaultFilterValues({});
    setClearData(true);
    // TODO: Replace with actual API call
    // await dispatch(
    //   getScheduledSurveysList({
    //     from_date: "",
    //     to_date: "",
    //     created_by: "",
    //     status: activeStatus,
    //   })
    // );
  };

  const handleSearch = (value, surveys) => {
    if (!value) return surveys;
    return surveys.filter(survey =>
      Object.values(survey)
        .join(" ")
        .toLowerCase()
        .includes(value.toLowerCase())
    );
  };

  const onIconClick = async id => {
    setSelectedFilterValues({});
    setDefaultFilterValues({});
    setClearData(true);
    // let parsedStatus = "";
    if (activeStatus === "" || activeStatus !== id) {
      // parsedStatus = id;
      setActiveStatus(id);
    } else {
      setActiveStatus("");
    }
    // TODO: Replace with actual API call
    // await dispatch(
    //   getScheduledSurveysList({
    //     from_date: "",
    //     to_date: "",
    //     created_by: "",
    //     status: parsedStatus,
    //   })
    // );
  };

  const handleRowClick = row => {
    navigate("/scheduled-survey-details", {
      state: { surveyData: row },
    });
  };

  const filteredSurveys = handleSearch(searchValue, surveyData);

  const scheduledSurveyColumns = [
    {
      name: "Survey Name",
      selector: row => row.name,
      minWidth: "16rem",
      sortable: true,
    },
    {
      name: "Title",
      selector: row => row.title,
      minWidth: "12rem",
      sortable: true,
    },
    {
      name: "Description",
      selector: row => row.description,
      minWidth: "16rem",
      sortable: true,
    },
    {
      name: CreatedBy,
      selector: row => (userMap ? userMap[row?.created_by]?.user_name : "-"),
      minWidth: "14rem",
      sortable: true,
    },
    {
      name: CreatedOn,
      selector: row => formatCustomDate(row.created_on),
      minWidth: "10rem",
      sortable: true,
    },
    {
      name: NoOfQuestions,
      selector: row => row.questions,
      minWidth: "8rem",
      sortable: true,
    },
    {
      name: StartDate,
      selector: row => formatCustomDate(row.start_date),
      minWidth: "10rem",
      sortable: true,
    },
    {
      name: EndDate,
      selector: row => formatCustomDate(row.end_date),
      minWidth: "10rem",
      sortable: true,
    },
    {
      name: Status,
      selector: row => (
        <div className={row.status}>
          {row.status === "ongoing"
            ? "Ongoing"
            : row.status === "upcoming"
              ? "Upcoming"
              : row.status === "completed"
                ? "Completed"
                : row.status}
        </div>
      ),
      minWidth: "10rem",
      sortable: true,
    },
  ];

  return (
    <div className="page-body scheduled-surveys-list">
      <TotalCountCard
        data={totalCountData}
        onIconClick={onIconClick}
        activeStatus={activeStatus}
        showColor={true}
      />
      <div>
        <Card>
          <CardBody>
            <div
              className={
                (!isMobile || isTab) && "d-flex justify-content-between"
              }
            >
              <H3 className="mt-3">Scheduled Surveys</H3>
              <div className="d-flex gap-2 align-items-center">
                <SearchBar
                  showSearch
                  onSearch={value => {
                    setClearData(false);
                    setSearchValue(value);
                  }}
                  clearOnProp={clearData}
                />
                <div>
                  <FilterSliderModal
                    dropdowns={[
                      {
                        label: StartDate,
                        key: "start_date",
                        tooltipText: StartDate,
                        isDate: true,
                        isMultiSelect: false,
                      },
                      {
                        label: EndDate,
                        key: "end_date",
                        tooltipText: EndDate,
                        isDate: true,
                        isMultiSelect: false,
                      },
                      {
                        label: CreatedBy,
                        key: "created_by",
                        tooltipText: `${Select} ${CreatedBy}`,
                        options: userMap
                          ? Object.values(userMap).map((user: any) => ({
                              name: user.id?.toString(),
                              value: user?.user_name,
                              label: user?.user_name,
                            }))
                          : [],
                        isMultiSelect: true,
                      },
                      {
                        label: Status,
                        key: "status",
                        tooltipText: `${Select} ${Status}`,
                        options: [
                          {
                            name: "ongoing",
                            value: "Ongoing",
                            label: "Ongoing",
                          },
                          {
                            name: "upcoming",
                            value: "Upcoming",
                            label: "Upcoming",
                          },
                          {
                            name: "completed",
                            value: "Completed",
                            label: "Completed",
                          },
                        ],
                        isMultiSelect: true,
                      },
                    ]}
                    selectedFilterValues={selectedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    setSelectedFilterValues={setSelectedFilterValues}
                    setDefaultFilterValues={setDefaultFilterValues}
                    onDone={handleDone}
                    onClear={handleClear}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3">
              <DataTable
                className="scheduled-surveys-list__table"
                columns={scheduledSurveyColumns}
                data={filteredSurveys}
                pagination
                fixedHeader
                striped
                onRowClicked={handleRowClick}
                customStyles={customTableStyles}
              />
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ScheduledSurveys;
